// One fluid layout for all four board shapes. Computes the card size and the slot positions from the
// board's size and writes them as CSS custom properties; cards AND slots are positioned from the same
// variables (board.css), so they can never disagree. Replaces the 6 width buckets x 4 games of
// swapped CSS files and the per-column 25/15 px step of the legacy app.
//
// Calibrated so that Klondike at 1280x900 reproduces the legacy 1280 layout: cards 109x144, column
// pitch 144 (gap 35), waste fan 20 px, tableau step 25 px (15 px when a column would overflow), top
// row at page y=95 and the tableau at y=335 (with the 40 px status strip above the board).
import type { GameId } from '../engine/types';
import { RULES } from '../engine/rules';
import { MAHJONG_LAYOUTS, type Layout } from '../engine/rules/mahjong';
import { CONNECT_LAYOUTS } from '../engine/rules/mahjongConnect';

export const CARD_RATIO = 144 / 109;
export const CARD_MAX_W = 109;
/** PROTOTYPE (owner 2026-10-23, big screens: "I hate the new version", 2555x1302 with 109 px cards on 40 % of
 *  the width): the card limit with `?bigcards=1` (options/assets.ts bigCardsProto). Not a default. */
export const BIG_CARD_MAX_W = 180;
/** Legacy ratios at 109x144. */
const GAP_RATIO = 35 / 109;
const GAP_RATIO_NARROW = 0.16;
const GAP_RATIO_MIN = 0.05;
/** The two-deck games (9 or 10 columns: Double Klondike, Spider, Forty Thieves) stand their columns 30 %
 *  closer when there is room to spare (owner 2026-10-17, iPad Pro: "przerwa pomiędzy kupkami trochę
 *  mniejsza, tak o 30 %", "to samo w każdej grze z 2 taliami"); where the width already limits the gap
 *  (desktop 1280, phones) nothing changes. */
const TWO_DECK_GAP_SCALE = 0.7;
/** The 10-column games (Spider, Forty Thieves) another 30 % closer (owner 2026-10-17: the stock sat too close
 *  to the screen edge on the iPad Pro): 0.7 x 0.7. */
const TEN_COLUMN_GAP_SCALE = 0.49;
const FAN_RATIO = 20 / 109;
/** The straight turn-three waste (small cards, the big-sign deck, the fan switched off): legacy's fixed 20 px, but
 *  never more than 0.4 of a card (legacy's 20 px of its 49 px phone card) (owner 2026-10-24, player report, iPhone 393 px:
 *  the tilted fan 6 px apart showed only the first sign of the rank). 0.4 shows the whole rank of the big-sign
 *  deck (its "10" ends at 0.28) and the rank and suit of the classic deck (0.18); it only takes the room left
 *  before the foundations (board.css --fan), so no card and no foundation moves. */
const FAN_RATIO_NARROW = 0.4;
/** The straight spread of small cards (under STRAIGHT_FAN_MAX_W) is this much of a card. */
const SMALL_SPREAD_MAX = 0.6;
/** A phone's Double Klondike: the turn-three spread, of a card (its eight foundations take the rest of the row). */
const DK_PHONE_SPREAD = 0.3;
/** ... which then takes the room the 1 px foundation gaps leave, up to this much of a card. */
const DK_PHONE_SPREAD_MAX = 0.6;
/** ... and its last foundation this close to the right edge, the stock 2 px from the left one, like legacy's (owner
 *  2026-10-24); the columns keep PHONE_PAD_X (the Android Back gesture starts at the edge). */
const DK_PHONE_TOP_PAD = 3;
/** ... and the gaps of its top row, of a card (2 px at least). */
const DK_PHONE_TOP_GAP = 0.05;
/** ... wherever the cards are small, not only on phones: a 576 px window on an iPad (owner 2026-10-24, screenshot:
 *  72 px cards, the tilted fan unreadable) kept the desktop fan. Full-size cards (109 px, desktops and big tablets)
 *  keep the tilted fan (owner: "wachlarz niech zostanie na większych ekranach"). */
const STRAIGHT_FAN_MAX_W = 80; // 80, not 90: an iPad's Double Klondike (89 px cards) has room for the fan (owner 2026-10-24)
const STEP_MAX_RATIO = 25 / 144;
/** Phones (the narrow board): the columns may spread further when the board has room below them, so the
 *  rank and suit corner of every covered card shows in full (owner 2026-10-18: "jak jest miejsce to
 *  rozciągaj bardziej kolumny"). The step still shrinks to fit the height (board.css --step). */
/** 0.4, not 0.3: legacy's 25 px on its 65 px phone card is 0.385, and 0.3 left a 384x474 phone's columns 20 px apart
 *  with a third of the screen empty (player report 2026-10-24, "rozkład kart na całym ekranie"). */
const STEP_MAX_RATIO_NARROW = 0.4;
/** Phones with the many-column games (9 or 10 columns: Double Klondike, Spider, Forty Thieves): the cards are
 *  narrow (35 px at 393), so the columns spread as far as legacy's (about half a card: 25 px of a 51 px card on
 *  its 480 layout), the corners of the covered cards readable (owner 2026-10-23, player report "karty za wąskie,
 *  nie da się grać na smartfonie"; the width stays: wider cards would push the stock off the screen). */
const STEP_MAX_RATIO_NARROW_MANY = 0.5;
/** ... and the stock (with the waste) keeps at least a quarter card from the screen edge past the side margin, like
 *  legacy's 5 px: its pile lifts up and left (board.css --lift) and nearly left the screen at 393 px. Only where the
 *  board reaches that close (Spider, Forty Thieves); Double Klondike's board is narrower (its top row limits the
 *  cards), so its stock stays on the column line. */
const STOCK_ROOM_NARROW_MANY = 0.25;
/** The same on wider boards, some 15-20 px past the side margin (owner 2026-10-23, 1024x600 Forty Thieves: the
 *  stock "nie dotykały tak mocno końca przeglądarki"). */
const STOCK_ROOM_MANY = 0.2;
const STEP_MIN_RATIO = 15 / 144;
const TOP_Y_RATIO = 55 / 144;
const ROW_GAP_RATIO = 96 / 144;
const TOP_Y_MAX = 55;
const ROW_GAP_MAX = 96;
/** A short, wide board (a phone on its side, a small landscape window): the height limits the cards, so the
 *  space above the top row and between the rows shrinks and the cards grow about 20 % (owner 2026-10-23,
 *  iPhone landscape: "wykorzystać przestrzeń obróconego telefonu"). */
const SHORT_TOP_Y_RATIO = 0.06;
const SHORT_ROW_GAP_RATIO = 0.22;
/** ... where the columns then stand (the cards are sized for SHORT_ROW_GAP_RATIO; the rest is the columns' room). */
const SHORT_ROW_GAP_PLACED = 0.06;
/** A very low board (a phone on its side with the browser bars, 963x268 in a player report 2026-10-23: 42 px
 *  cards, "jak można powiększyć karty"): the top row moves off the top. Stock and waste stand in a strip on the
 *  left, the foundations in two rows on the right, and the columns get the whole height (68 px cards there).
 *  Only below this board height (844x390 keeps the stacked layout the owner approved the same day), and only
 *  when the cards come out clearly bigger than stacked (SIDE_LAYOUT_GAIN; a low but narrow board such as
 *  412x290 has no room across for the two side blocks). */
export const SIDE_LAYOUT_MAX_H = 300;
const SIDE_LAYOUT_GAIN = 1.1;
/** Room kept left of the stock strip for the Privacy tab (app.css: a 30 px shield, always in the bottom left
 *  corner of the screen, owner 2026-10-23: "zawsze przykleja do lewej strony na dół"; without the room it
 *  covered the waste). */
const SIDE_CORNER_W = 36;
const PHONE_PAD_X = 12;
/** The smallest step a long column closes up to (of a card's height): low boards size their cards for a column
 *  of 13 at this step, not at STEP_MIN_RATIO, so the cards come out bigger, nearer legacy's (which kept no room for
 *  long columns at all); a column closes up below the old minimum only when it really is that long (owner
 *  2026-10-24, a player at 755x425: "Jak zwiększyć rozmiar kart?", 76 -> 89 px cards). */
const STEP_FLOOR_RATIO = 0.06;
/** Height a column must be able to show: a card plus 12 steps at the floor step. */
const COLUMN_UNITS = 1 + 12 * STEP_FLOOR_RATIO;
/** The side layout of a very low board keeps the full minimum step (its two rows of foundations need the height);
 *  so does the squeeze of the gaps on a low board. */
const SIDE_COLUMN_UNITS = 1 + 12 * STEP_MIN_RATIO;

export interface Geometry {
  cardW: number;
  cardH: number;
  gap: number;
  pitch: number;
  fan: number;
  /** The turn-three waste lies straight, `fan` apart (small cards: FAN_RATIO_NARROW, board.css data-fan). */
  straightFan: boolean;
  stepMax: number;
  stepMin: number;
  topY: number;
  tableauY: number;
  tableauX0: number;
  stockX: number;
  wasteX: number;
  foundX0: number;
  foundPitch: number;
  /** The foundations stand in rows of this many (all of them in one row, except the side layout: two rows). */
  foundCols: number;
  foundY0: number;
  foundRowPitch: number;
  wasteY: number;
  /** FreeCell's free cells: laid out like the foundations (a row over the first columns; two rows in the side
   *  layout). 0 cells in the other games (the values are unused there). */
  cells: number;
  cellX0: number;
  cellPitch: number;
  cellCols: number;
  cellY0: number;
  cellRowPitch: number;
  /** Stock and waste in a strip left of the columns, foundations right of them (SIDE_LAYOUT_MAX_H). */
  side: boolean;
  colH: number;
  cols: number;
  found: number;
  hasWaste: boolean;
  /** Pyramid: how far each row of the pyramid stands below the one above it (0 in the other games). */
  pyrStep: number;
  /** Mahjong: half a tile's face across and down (the layout's grid), and how far each layer lies up and left of the
   *  one under it (0 in the other games). */
  tileHalfW: number;
  tileHalfH: number;
  tileLayer: number;
}

/** `bottomReserve`: the part of the board's bottom an anchor ad covers (AdAnchorBand.tsx). The cards and the
 *  slots are laid out on the full height; only the columns stop above it (their step compresses). */
/** `straightFan`: the turn-three waste lies straight whatever the card size (the Options fan switch off, or the
 *  big-sign deck: its "10" needs 0.28 of a card; owner 2026-10-24). */
/** Turn three lies straight (board.css data-fan="straight") with the fan switched off or the big-sign deck
 *  (`straightFan`), in the Classic mode (the Classic fan lies flat anyway, app.css; a new turn of three covers the
 *  last three where they lie, as in legacy; owner 2026-10-24: fast clicks slid them to the left) and with cards under
 *  STRAIGHT_FAN_MAX_W. */
function isStraight(rules: (typeof RULES)[GameId], cardW: number, classic: boolean, straightFan: boolean): boolean {
  return (straightFan || classic || cardW < STRAIGHT_FAN_MAX_W) && rules.hasWaste && rules.turn === 3;
}

/** The straight spread's px between the three: legacy's fixed 20 px (0.41 of its phone card, 0.18 of the desktop
 *  one), at least 0.2 of a card (a 0.4 card gap on a tablet was too wide, owner 2026-10-24); the Classic mode's own
 *  20 px of a 109 px card where the fan would show. The big-sign deck 0.48: it has no corner suit, and at least 40 %
 *  of the big centre sign of each covered card shows (owner 2026-10-24; the sign spans 0.38-0.62 of the card for
 *  diamonds, 0.26-0.74 for spades). */
function spreadGap(cardW: number, classic: boolean, straightFan: boolean, bigSigns: boolean): number {
  if (classic && !straightFan && cardW >= STRAIGHT_FAN_MAX_W) return Math.round((cardW * 20) / 109);
  return Math.round(Math.max(cardW * (bigSigns ? 0.48 : 0.2), Math.min(20, cardW * FAN_RATIO_NARROW)));
}

/** `classic`: the Classic mode (ui/effects.ts, 3D effects off): the 9 and 10 column games stand their columns
 *  side by side, a centred block like legacy (owner 2026-10-24, three player reports with the columns spread to the
 *  screen edges); phones keep their layout. */
/** `layout`: Mahjong's layout of the deal (GameState.layout). */
export function computeGeometry(game: GameId, width: number, height: number, bottomReserve = 0, maxW = CARD_MAX_W, classic = false, straightFan = false, bigSigns = false, narrowCols = classic, layout = 0): Geometry {
  // the fixed caps of the legacy 1280 layout grow with a bigger card limit (the prototype)
  const capScale = maxW / CARD_MAX_W;
  const topYMax = TOP_Y_MAX * capScale;
  const rowGapMax = ROW_GAP_MAX * capScale;
  const rules = RULES[game];
  if (rules.tiles) return mahjongGeometry(width, height, maxW, rules.layouts?.[layout] ?? rules.layouts?.[0] ?? MAHJONG_LAYOUTS[0]!);
  if (rules.pairs) return pyramidGeometry(game, width, height, bottomReserve, maxW, narrowCols);
  const cols = rules.columns.length;
  const found = rules.foundations;
  const cells = rules.cells;
  const hasWaste = rules.hasWaste;
  const narrow = width < 560;
  // the columns side by side (Options "Column spacing: narrow", by default in the Classic mode: `narrowCols`), every
  // game and every game to come (owner 2026-10-24); phones keep their own spacing
  const tight = narrowCols && !narrow;
  const gapRatio = tight ? 0 : (narrow ? GAP_RATIO_NARROW : GAP_RATIO) * (cols >= 10 ? TEN_COLUMN_GAP_SCALE : rules.decks === 2 ? TWO_DECK_GAP_SCALE : 1);
  // Phones keep 12 px from the side edges (owner 2026-10-23, player reports "jest do niczego" from 360 px
  // phones: the board ran edge to edge, 2 px, unlike the legacy 30 px, and the Android Back gesture starts
  // at the left edge, where the first column was dragged); cards 48 -> 46 px at 360.
  // a phone's Double Klondike 1 px closer to the edges: room for its eight foundations (owner 2026-10-24)
  const dkPhone = narrow && game.startsWith('double-klondike');
  const padX = narrow ? PHONE_PAD_X - (dkPhone ? 1 : 0) : Math.max(4, Math.min(24, width * 0.012));
  const padY = narrow ? 6 : 12;
  const short = height < 460 && width >= height * 1.2;
  const topYRatio = short ? SHORT_TOP_Y_RATIO : TOP_Y_RATIO;
  const rowGapRatio = short ? SHORT_ROW_GAP_RATIO : ROW_GAP_RATIO;

  // Width limits: the tableau row and the top row (stock, waste + fan, foundations packed tightly).
  // Full-size cards come before the column gap: the gap shrinks down to GAP_RATIO_MIN before the
  // cards do (legacy 1280 bucket: 10-column games had 109 px cards nearly touching).
  const wFromCols = (width - 2 * padX) / (cols + GAP_RATIO_MIN * (cols - 1));
  // a phone's Double Klondike (`dkPhone`): the eight foundations side by side, never on one another, and the cards as
  // big as the top row then allows, with the turn-three spread at DK_PHONE_SPREAD of a card (owner 2026-10-24: the
  // foundations closed up to 0.7 of a card for 38 px cards overlapped, "critical"; he chose 33 px cards)
  const spreadUnits = rules.turn === 3 ? 2 * (dkPhone ? DK_PHONE_SPREAD : FAN_RATIO) : 0;
  const topUnits = (cells ? cells : hasWaste ? 2 + spreadUnits : 1) + found;
  const wFromTop = (width - 2 * padX) / (topUnits + (dkPhone ? DK_PHONE_TOP_GAP : 0.08) * (topUnits - 1));
  let cardW = Math.min(wFromCols, wFromTop, maxW);

  // Height limit: top row + row gap + a column of 13 cards at the minimum step. Like legacy's "wide" mode
  // (windows under 680 px kept 109 px cards), the space above the top row and between the rows shrinks
  // first, down to the short-board ratios (squeeze 0 -> 1), and only then the cards (player report
  // 2026-10-23, 1143x574: "były duże, teraz są małe", 89 px cards against legacy's 109).
  const spaceAt = (h: number, squeeze: number) => {
    const top = Math.min(topYMax, topYRatio * h);
    const rowGap = Math.min(rowGapMax, rowGapRatio * h);
    return { top: top + (SHORT_TOP_Y_RATIO * h - top) * squeeze, rowGap: rowGap + (SHORT_ROW_GAP_RATIO * h - rowGap) * squeeze };
  };
  const heightNeeded = (w: number, squeeze: number, units = COLUMN_UNITS) => {
    const h = w * CARD_RATIO;
    const sp = spaceAt(h, squeeze);
    return padY + sp.top + h + sp.rowGap + h * units + padY;
  };
  // the squeeze still counts the column at the full minimum step (SIDE_COLUMN_UNITS): sized at the floor step, a
  // 600 px board stopped counting as low and got the desktop's gaps back (the columns' room going to the gaps); only
  // the cards' size counts on the floor step
  let squeeze = 0;
  if (heightNeeded(cardW, 0, SIDE_COLUMN_UNITS) > height) {
    const full = heightNeeded(cardW, 0, SIDE_COLUMN_UNITS);
    const tight = heightNeeded(cardW, 1, SIDE_COLUMN_UNITS);
    squeeze = full > tight ? Math.min(1, (full - height) / (full - tight)) : 1;
    // Scale down (two passes are enough: the capped parts make it slightly non-linear).
    if (heightNeeded(cardW, squeeze) > height) for (let i = 0; i < 3; i++) cardW = Math.max(20, cardW * (height / heightNeeded(cardW, squeeze)));
  }
  cardW = Math.floor(cardW);
  const cardH = Math.round(cardW * CARD_RATIO);
  const gapLeft = (width - 2 * padX - cols * cardW) / Math.max(1, cols - 1);
  // classic: legacy's 109 px pitch for 109 px cards (the card pictures have their own rounded margin)
  // a phone's Double Klondike: the columns spread over the full width, under the edge-to-edge top row (owner 2026-10-24:
  // room was left on both sides)
  const gap = tight ? 0 : dkPhone ? Math.max(Math.round(cardW * GAP_RATIO_MIN), Math.floor(gapLeft)) : Math.round(Math.max(cardW * GAP_RATIO_MIN, Math.min(cardW * gapRatio, gapLeft)));
  const pitch = cardW + gap;
  const fan = Math.round(cardW * FAN_RATIO);
  const many = narrow && cols >= 9;
  const stepMax = Math.round(cardH * (many ? STEP_MAX_RATIO_NARROW_MANY : narrow ? STEP_MAX_RATIO_NARROW : STEP_MAX_RATIO));
  const stepMin = Math.round(cardH * STEP_FLOOR_RATIO);
  const space = spaceAt(cardH, squeeze);
  // a low board's top row 4 px higher (owner 2026-10-24: room for the columns)
  const topY = Math.max(Math.round(padY - 4 * squeeze), Math.round(space.top));
  // a low board's columns stand closer to the top row than its card size counted on (SHORT_ROW_GAP_PLACED, owner
  // 2026-10-24): the room goes to the columns, which then close up less
  const rowGapPlaced = space.rowGap - (SHORT_ROW_GAP_RATIO - SHORT_ROW_GAP_PLACED) * cardH * squeeze;
  const tableauY = topY + cardH + Math.round(rowGapPlaced);
  const boardW = cols * cardW + (cols - 1) * gap;
  // Centred, 1 px to the right: legacy framed the board in a 1020 px box with a 25 px margin, which
  // put the 1280x900 Klondike board at x 155 (not 153.5); kept so the default-size baselines hold.
  const tableauX0 = Math.round((width - boardW) / 2) + 1;
  const right = tableauX0 + boardW;

  // Top row: stock on the first column, the waste next to it, foundations right-aligned.
  const stockRoom = cols >= 9 ? (narrow ? STOCK_ROOM_NARROW_MANY : STOCK_ROOM_MANY) : 0;
  let stockX = stockRoom ? Math.max(tableauX0, Math.round(padX + cardW * stockRoom)) : tableauX0;
  // a phone's Double Klondike: its top row 2 px apart (the columns keep their gap), room for the eight foundations
  // the narrow columns with the 3D effects (Options, owner 2026-10-24): the tilted turn-three fan reaches left of the
  // waste's place and the stock has its thickness, so the waste keeps the gap of the foundations from the stock (it
  // touched it: "kupka nachodzi na karty po 3"); the foundations keep their own gap
  const tiltedFan = rules.turn === 3 && hasWaste && !isStraight(rules, cardW, classic, straightFan);
  const topRowGap = dkPhone ? Math.max(2, Math.round(cardW * DK_PHONE_TOP_GAP)) : gap;
  const topRowPitch = cardW + topRowGap;
  // the fan reaches 0.17 of a card left of the waste's place; Double Klondike's stands 0.15 further right (board.css
  // --fan-shift): about 0.18 of a card of table stays between it and the stock, as with the wide spacing
  // with the 3D effects the stock and the waste are thick piles (board.css --lift, up to 0.11 of a card): side by side
  // they looked to lie on one another (owner 2026-10-24, Forty Thieves), so they keep 0.15 of a card apart; the flat
  // Classic piles stand side by side like legacy
  // any spacing: the tilted fan keeps that much table from the stock (owner 2026-10-24: on a low board it nearly
  // touched it); 0.32 = the legacy 1280 layout's gap, so the desktop stays as it was
  const fanClear = cardW + Math.round(cardW * (game.startsWith('double-klondike') ? 0.2 : 0.32));
  const wastePitch = tiltedFan ? Math.max(tight ? 0 : topRowPitch, fanClear) : tight && !classic ? cardW + Math.round(cardW * 0.15) : topRowPitch;
  const topGap = tight ? Math.round(cardW * 0.2) : topRowGap;
  // FreeCell: the free cells over the first columns, the foundations right-aligned (no stock, no waste; the deck
  // of the deal stands on the first cell)
  const straight = isStraight(rules, cardW, classic, straightFan);
  // small cards (phones) spread wider, up to SMALL_SPREAD_MAX of a card, so more of each covered card shows (owner
  // 2026-10-24: "trochę rozciągnij"; legacy's 20 px of its 36 px phone card: 0.55)
  const straightGap = dkPhone ? Math.round(cardW * DK_PHONE_SPREAD) : cardW < STRAIGHT_FAN_MAX_W ? Math.max(spreadGap(cardW, classic, straightFan, bigSigns), Math.round(cardW * SMALL_SPREAD_MAX)) : spreadGap(cardW, classic, straightFan, bigSigns);
  // the turn-three spread's room: the tilted fan's, or the wider straight one's for small cards and the big-sign deck
  // (owner 2026-10-24: on a phone's Double Klondike the three lay 6 px apart); the fan switched off alone moves
  // nothing on the board (owner 2026-10-24: an iPad's foundations spread out when it was switched off)
  const roomy = straight && (cardW < STRAIGHT_FAN_MAX_W || bigSigns);
  const wasteW = cells ? (cells - 1) * pitch + cardW : hasWaste ? wastePitch + cardW + (rules.turn === 3 ? 2 * (roomy ? Math.max(fan, straightGap) : fan) : 0) : cardW;
  // a phone's Double Klondike: its foundations 1 px apart, the room left goes to the turn-three spread (owner 2026-10-24)
  const foundGap = dkPhone ? 1 : topRowGap;
  const needed = found * cardW + (found - 1) * foundGap;
  let topRight = right;
  // classic, and the roomy straight spread: a top row wider than the columns sticks out on both sides, like legacy's Double
  // Klondike (the stock left of the first column, the foundations past the last one), within the side margins; only
  // then the foundations close up (owner 2026-10-24: "tę kupkę przenieść bardziej na lewo")
  // the straight spread keeps the clearance before the foundations its room is measured with (fanRoom)
  const clear = roomy ? Math.max(topGap, Math.round(cardW * 0.1)) : topGap;
  const over = stockX + wasteW + clear + needed - right;
  if ((tight || roomy) && over > 0) {
    const left = Math.min(Math.ceil(over / 2), Math.floor(stockX - padX)); // whole px: padX may be a fraction
    stockX -= left;
    topRight = Math.min(width - padX, right + over - left);
  }
  // a phone's Double Klondike: the top row from edge to edge (the stock and the last foundation on the margins), all
  // the room between them for the turn-three spread
  if (dkPhone) {
    // the stock's top card lies raised up-left by its pile's thickness (board.css --lift, at most 0.11 of a card): 2 px
    // of table stay in view left of it (owner 2026-10-24)
    stockX = 2 + Math.ceil(cardW * 0.11);
    topRight = Math.floor(width - DK_PHONE_TOP_PAD);
  }
  const wasteX = stockX + wastePitch;
  const minStart = stockX + wasteW + clear;
  let foundPitch = cardW + foundGap;
  if (topRight - minStart < needed) foundPitch = Math.max(cardW * 0.55, (topRight - minStart - cardW) / Math.max(1, found - 1));
  const foundX0 = Math.round(topRight - (cardW + (found - 1) * foundPitch));
  // a low board's columns reach down to 2 px over its bottom (owner 2026-10-24: 12 px stayed empty)
  const colH = Math.max(cardH, height - bottomReserve - tableauY - Math.round(padY - (padY - 2) * squeeze));

  const fanRoom = Math.floor((foundX0 - Math.max(dkPhone ? topRowGap : gap, Math.round(cardW * 0.1)) - wasteX - cardW) / 2);
  // never more than the room before the foundations
  const shownFan = straight ? Math.min(dkPhone ? Math.round(cardW * DK_PHONE_SPREAD_MAX) : straightGap, Math.max(fan, fanRoom)) : fan;

  const stacked: Geometry = { cardW, cardH, gap, pitch, fan: shownFan, straightFan: straight, stepMax, stepMin, topY, tableauY, tableauX0, stockX, wasteX, foundX0, foundPitch: Math.round(foundPitch), foundCols: found, foundY0: topY, foundRowPitch: 0, wasteY: topY, cells, cellX0: tableauX0, cellPitch: pitch, cellCols: Math.max(1, cells), cellY0: topY, cellRowPitch: 0, side: false, colH, cols, found, hasWaste, pyrStep: 0, tileHalfW: 0, tileHalfH: 0, tileLayer: 0 };
  if (height >= SIDE_LAYOUT_MAX_H) return stacked;
  const side = sideGeometry(game, width, height, padX, padY, gapRatio, bottomReserve, maxW, classic, straightFan, bigSigns);
  return side.cardW > stacked.cardW * SIDE_LAYOUT_GAIN ? side : stacked;
}

/** The side layout: [stock / waste] [columns] [foundations, 2 rows], each block a gap apart, all centred. */
function sideGeometry(game: GameId, width: number, height: number, padX: number, padY: number, gapRatio: number, bottomReserve: number, maxW: number, classic: boolean, straightFan: boolean, bigSigns: boolean): Geometry {
  const rules = RULES[game];
  const cols = rules.columns.length;
  const found = rules.foundations;
  const cells = rules.cells;
  const hasWaste = rules.hasWaste;
  const foundCols = Math.ceil(found / 2);
  // FreeCell: the strip on the left is the free cells' block, two rows like the foundations
  const cellCols = cells ? Math.ceil(cells / 2) : 1;
  const turn3 = hasWaste && rules.turn === 3;
  const room = width - 2 * padX - SIDE_CORNER_W;
  // down: a column of 13 cards at the minimum step; the strip and the foundation block (two cards) fit under that
  const wFromHeight = (height - 2 * padY) / CARD_RATIO / SIDE_COLUMN_UNITS;
  // across: the strip (a card, plus the turn-three spread), the columns, the foundation block, with the narrowest gaps
  const widthFor = (spreadUnits: number) =>
    Math.max(20, Math.floor(Math.min(room / (cellCols + spreadUnits + cols + foundCols + GAP_RATIO_MIN * (cols - 1 + foundCols - 1 + cellCols - 1 + 2)), wFromHeight, maxW)));
  let cardW = widthFor(turn3 ? 2 * FAN_RATIO : 0);
  // the straight spread (computeGeometry) under the stock, to the right, with its own wider room (owner 2026-10-24: the
  // tilted fan of a phone held sideways showed the big-sign deck's covered cards 10 px wide)
  const straight = isStraight(rules, cardW, classic, straightFan);
  if (straight) cardW = widthFor((2 * spreadGap(cardW, classic, straightFan, bigSigns)) / cardW);
  const cardH = Math.round(cardW * CARD_RATIO);
  const fan = straight ? spreadGap(cardW, classic, straightFan, bigSigns) : Math.round(cardW * FAN_RATIO);
  const fanUnits = turn3 ? (2 * fan) / cardW : 0;
  const blocks = cols + foundCols - 1 + cellCols - 1 + 2; // gaps: between the columns, the foundations, the cells, and the two block edges
  const gapLeft = (room - (cellCols + fanUnits) * cardW - (cols + foundCols) * cardW) / blocks;
  const gap = Math.round(Math.max(cardW * GAP_RATIO_MIN, Math.min(cardW * gapRatio, gapLeft)));
  const pitch = cardW + gap;
  const stripW = cells ? cellCols * cardW + (cellCols - 1) * gap : cardW + (fanUnits ? 2 * fan : 0);
  const totalW = stripW + gap + cols * cardW + (cols - 1) * gap + gap + foundCols * cardW + (foundCols - 1) * gap;
  const stockX = SIDE_CORNER_W + Math.round((width - SIDE_CORNER_W - totalW) / 2);
  const tableauX0 = stockX + stripW + gap;
  const foundX0 = tableauX0 + cols * pitch;
  const vGap = Math.max(4, Math.min(gap, height - 2 * padY - 2 * cardH));
  return {
    cardW,
    cardH,
    gap,
    pitch,
    fan,
    straightFan: straight,
    stepMax: Math.round(cardH * STEP_MAX_RATIO),
    stepMin: Math.round(cardH * STEP_MIN_RATIO),
    topY: padY,
    tableauY: padY,
    tableauX0,
    stockX,
    wasteX: stockX,
    foundX0,
    foundPitch: pitch,
    foundCols,
    foundY0: padY,
    foundRowPitch: cardH + vGap,
    wasteY: padY + cardH + vGap,
    cells,
    cellX0: stockX,
    cellPitch: pitch,
    cellCols,
    cellY0: padY,
    cellRowPitch: cardH + vGap,
    side: true,
    colH: Math.max(cardH, height - bottomReserve - 2 * padY),
    cols,
    found,
    hasWaste,
    pyrStep: 0,
    tileHalfW: 0,
    tileHalfH: 0,
    tileLayer: 0,
  };
}

/** Pyramid: each row half a card below the one above; a height-limited board closes the rows up to this. */
const PYR_STEP_RATIO = 0.5;
const PYR_STEP_MIN_RATIO = 0.36;
/** A tall phone (the cards limited by the width, room to spare below): the rows 25 % further apart (owner 2026-10-24:
 *  "na telefonie (długi ekran) rozciągnąłbym karty w dół o 25%"), so more of each covered card shows. */
const PYR_STEP_TALL_SCALE = 1.25;
/** The gap between the cards of a row, of a card (the narrow column spacing: PYR_GAP_NARROW). */
const PYR_GAP_RATIO = 0.14;
const PYR_GAP_NARROW = 0.06;

/**
 * Pyramid: 7 rows, the bottom one 7 cards wide; the stock and the waste stand in the empty top left corner beside the
 * top rows, the discard pile in the top right one (as in Microsoft's Pyramid), so the pyramid takes the whole height.
 * The rows stand a step apart (the cards below cover the lower part of each card, its rank corner stays in view). The
 * anchor ad's band is ignored, as in every game (owner 2026-10-24: "zawsze ignorujemy reklamę dolną", the player can
 * close or hide it); `_bottomReserve` is kept for the common signature. Each card is placed by
 * its row and its place in half pitches (board.css, Board cardRecords: --col = 2 x place - row + 6).
 */
function pyramidGeometry(game: GameId, width: number, height: number, _bottomReserve: number, maxW: number, narrowCols: boolean): Geometry {
  const rules = RULES[game];
  const narrow = width < 560;
  const padX = narrow ? PHONE_PAD_X : Math.max(4, Math.min(24, width * 0.012));
  const padY = narrow ? 6 : 12;
  const gapRatio = narrowCols && !narrow ? PYR_GAP_NARROW : PYR_GAP_RATIO;
  const rows = 7;
  const room = height;
  const wFromCols = (width - 2 * padX) / (rows + gapRatio * (rows - 1));
  // the space above the pyramid like the other games' top row, shrinking first on a low board (squeeze 0 -> 1)
  const heightAt = (w: number, squeeze: number) => {
    const h = w * CARD_RATIO;
    const top = Math.min(TOP_Y_MAX * (maxW / CARD_MAX_W), TOP_Y_RATIO * h) * (1 - squeeze);
    const step = h * (PYR_STEP_RATIO - (PYR_STEP_RATIO - PYR_STEP_MIN_RATIO) * squeeze);
    return padY + top + h + (rows - 1) * step + padY;
  };
  let cardW = Math.min(wFromCols, maxW);
  let squeeze = 0;
  if (heightAt(cardW, 0) > room) {
    const full = heightAt(cardW, 0);
    const tight = heightAt(cardW, 1);
    squeeze = Math.min(1, (full - room) / (full - tight));
    if (heightAt(cardW, squeeze) > room) for (let i = 0; i < 3; i++) cardW = Math.max(20, cardW * (room / heightAt(cardW, squeeze)));
  }
  cardW = Math.floor(cardW);
  const cardH = Math.round(cardW * CARD_RATIO);
  const gap = Math.round(cardW * gapRatio);
  const pitch = cardW + gap;
  let pyrStep = Math.round(cardH * (PYR_STEP_RATIO - (PYR_STEP_RATIO - PYR_STEP_MIN_RATIO) * squeeze));
  if (narrow && squeeze === 0) {
    // only as far as the board has room for
    const tall = Math.round(pyrStep * PYR_STEP_TALL_SCALE);
    const fits = Math.floor((room - heightAt(cardW, 0) + (rows - 1) * pyrStep) / (rows - 1));
    pyrStep = Math.max(pyrStep, Math.min(tall, fits));
  }
  const topY = Math.round(padY + Math.min(TOP_Y_MAX * (maxW / CARD_MAX_W), TOP_Y_RATIO * cardH) * (1 - squeeze));
  const boardW = rows * cardW + (rows - 1) * gap;
  const tableauX0 = Math.round((width - boardW) / 2) + 1;
  const stockX = tableauX0;
  const wasteX = tableauX0 + pitch;
  const foundX0 = tableauX0 + (rows - 1) * pitch;
  return {
    cardW,
    cardH,
    gap,
    pitch,
    fan: Math.round(cardW * FAN_RATIO),
    straightFan: false,
    stepMax: 0,
    stepMin: 0,
    topY,
    tableauY: topY,
    tableauX0,
    stockX,
    wasteX,
    foundX0,
    foundPitch: pitch,
    foundCols: 1,
    foundY0: topY,
    foundRowPitch: 0,
    wasteY: topY,
    cells: 0,
    cellX0: tableauX0,
    cellPitch: pitch,
    cellCols: 1,
    cellY0: topY,
    cellRowPitch: 0,
    side: false,
    colH: cardH,
    cols: rows,
    found: rules.foundations,
    hasWaste: true,
    pyrStep,
    tileHalfW: 0,
    tileHalfH: 0,
    tileLayer: 0,
  };
}

/** Mahjong's tile pictures (frontend/scripts/tiles/make-tiles.mjs): 146 x 186, the face 112 x 152 at (1, 1), the 3D side
 *  (10) and a soft shadow the rest (board.css uses the same numbers). */
const TILE_IMG_W = 146;
const TILE_IMG_H = 186;
/** The part of the picture the layout counts: the face and the side (the shadow may reach over the board's padding). */
const TILE_BODY_W = 122;
const TILE_FACE_W = 112;
const TILE_FACE_H = 152;
/** How far a layer lies up and left of the one under it, of a face: almost twice the side, so a stack reads as steps
 *  from every side (owner 2026-10-25, the layer look A5: was 11 / 112). */
const TILE_LAYER_RATIO = 20 / 112;
/** The biggest tile face, of the card limit (a desktop Turtle is limited by the height anyway). */
const TILE_MAX_RATIO = 0.9;

/** The tile face's width a layout gets on a board (the layout's width and height in faces: the tiles, the last tile's
 *  side, the top layer's offset). */
/** The gap kept between the discard pile and any tile, past the tile's side, of a face (at least DISCARD_GAP_MIN px). */
const DISCARD_CLEAR = 0.2;
const DISCARD_GAP_MIN = 4;
/** A tile's body (its face and its 3D side, no shadow) and the discard pile's place, in faces. */
const BODY_W = TILE_BODY_W / TILE_FACE_W;
const BODY_H = (TILE_FACE_H + TILE_BODY_W - TILE_FACE_W) / TILE_FACE_W;

/**
 * The discard pile's place on any shape (owner 2026-10-27: "generycznie, aby działało na wszystkich i przyszłych
 * kształtach"; in the Fort and the Cat it touched the corner tower / the ear and stood lower than its top, and the
 * Tower's place was its own rule). It starts over the last column, its top level with the highest tile of the top two
 * rows; every tile it would touch (DISCARD_CLEAR kept) is then stepped round, past the tile's right side or over its
 * top, whichever is the shorter step, until none is touched. So an empty corner keeps it there (the Turtle, the
 * Pyramid, the Tree), a corner tile puts it beside it (the Fort, the Cat), and a tile below and one beside lift it a
 * little and move it a little, the same gap to both (the Tower). In faces from the layout's top left corner (layer 0),
 * `y` in face widths; `layer` and `row` are the steps in faces (the real pixel ones in mahjongGeometry).
 */
export function discardSpot(l: Layout, clear = DISCARD_CLEAR, layer = TILE_LAYER_RATIO, row = TILE_FACE_H / TILE_FACE_W): { x: number; y: number } {
  const tiles = l.places.map((p) => ({ left: p.x / 2 - p.z * layer, top: (p.y / 2) * row - p.z * layer }));
  let x = l.w / 2 - 1;
  let y = Math.min(...l.places.filter((p) => p.y < 4).map((p) => (p.y / 2) * row - p.z * layer));
  for (let step = 0; step < 20; step++) {
    let right = x;
    let up = y;
    for (const t of tiles) {
      const touches = t.left < x + BODY_W + clear && t.left + BODY_W + clear > x && t.top < y + BODY_H + clear && t.top + BODY_H + clear > y;
      if (!touches) continue;
      if (t.left + BODY_W + clear - x <= y + BODY_H + clear - t.top) right = Math.max(right, t.left + BODY_W + clear);
      else up = Math.min(up, t.top - BODY_H - clear);
    }
    if (right === x && up === y) break;
    x = right;
    y = up;
  }
  return { x, y };
}
/** The room the pile takes past the layout: on the right (past the last tile's side) and above (past the top layer). */
const discardRoom = (l: Layout): { right: number; above: number } => {
  const spot = discardSpot(l);
  return { right: Math.max(0, spot.x + BODY_W - (l.w / 2 + BODY_W - 1)), above: Math.max(0, -l.layers * TILE_LAYER_RATIO - spot.y) };
};

function mahjongFace(l: Layout, width: number, height: number, maxW: number): { faceW: number; wFaces: number; hFaces: number; padY: number; padX: number } {
  const narrow = width < 560;
  const padX = narrow ? PHONE_PAD_X : Math.max(4, Math.min(24, width * 0.012));
  const padY = narrow ? 6 : 12;
  // the last row's and the last column's whole picture, shadow included: a shadow past the board's edge let the page
  // scroll on an iPad (owner 2026-10-24: "Mahjong ma scrolla na iPad")
  const rightRatio = (TILE_IMG_W - 1 - TILE_FACE_W) / TILE_FACE_W;
  const belowRatio = (TILE_IMG_H - 1 - TILE_FACE_H) / TILE_FACE_W;
  const room = discardRoom(l);
  // Connect: half a tile free round the grid, where the joining line may run (Layout.margin)
  const margin = 2 * (l.margin ?? 0);
  const wFaces = l.w / 2 + rightRatio + l.layers * TILE_LAYER_RATIO + room.right + margin;
  const hFaces = (l.h / 2) * (TILE_FACE_H / TILE_FACE_W) + belowRatio + l.layers * TILE_LAYER_RATIO + room.above + margin;
  const faceW = Math.max(10, Math.floor(Math.min((width - 2 * padX) / wFaces, (height - 2 * padY) / hFaces, maxW * TILE_MAX_RATIO)));
  return { faceW, wFaces, hFaces, padY, padX };
}

/** A layout fits a board when its tiles are at least this share of the biggest tiles a layout gets there: the Tree on
 *  a phone held upright gets 77-81 % of the Tower's, the Tower on a computer 74-75 % of the Turtle's (2026-10-27). */
const MAHJONG_FIT = 0.76;
/** ... or when its tile face is this wide anyway (owner 2026-10-27, iPad: the Tree, 65 % of the Turtle there, "dobrze
 *  pasuje, płytki nie są za małe"): every shape on a computer and an iPad, still not the wide ones on an upright phone. */
const MAHJONG_FIT_PX = 36;

/** The layouts a new Mahjong deal may take on this board, one of them at random (owner 2026-10-27, "b": each game
 *  looks different and nobody gets tiny tiles); the Games window marks the others "small tiles on this screen". */
export function mahjongLayoutsFor(width: number, height: number, maxW = CARD_MAX_W): number[] {
  const faces = MAHJONG_LAYOUTS.map((l) => mahjongFace(l, width, height, maxW).faceW);
  const best = Math.max(...faces);
  return faces.flatMap((f, i) => (f >= best * MAHJONG_FIT || f >= MAHJONG_FIT_PX ? [i] : []));
}

/** The Turtle or the Tower, whichever has the bigger tiles on this board (owner 2026-10-24: the Tower on an upright
 *  phone or tablet, where the Turtle's tiles were tiny): the rule before the random pick, kept for the e2e tests. */
export function mahjongLayoutFor(width: number, height: number, maxW = CARD_MAX_W): number {
  let best = 0;
  MAHJONG_LAYOUTS.slice(0, 2).forEach((l, i) => {
    if (mahjongFace(l, width, height, maxW).faceW > mahjongFace(MAHJONG_LAYOUTS[best]!, width, height, maxW).faceW) best = i;
  });
  return best;
}

/** Mahjong Connect: the wide grid (18 x 8) or the tall one (8 x 18), whichever has the bigger tiles on this board
 *  (engine/rules/mahjongConnect.ts CONNECT_LAYOUTS; a phone or a tablet held upright gets the tall one). */
export function connectLayoutFor(width: number, height: number, maxW = CARD_MAX_W): number {
  const faces = CONNECT_LAYOUTS.map((l) => mahjongFace(l, width, height, maxW).faceW);
  return faces.indexOf(Math.max(...faces));
}

/**
 * Mahjong: the deal's layout (engine/rules/mahjong.ts: the Turtle 15 x 8 tiles, the Tower 8 x 11, ...), centred on the
 * board, as big as it fits. The deck before the deal and the discard pile stand in the layout's empty top corners. The
 * anchor ad's band is ignored, as in every game (owner 2026-10-24). A card's size here is the tile picture's, side and
 * shadow included; board.css places each tile by its place in half faces (--col, --row) and its layer.
 */
function mahjongGeometry(width: number, height: number, maxW: number, l: Layout): Geometry {
  const { faceW, wFaces, hFaces, padY, padX } = mahjongFace(l, width, height, maxW);
  const across = l.w / 2;
  const layers = l.layers;
  const faceH = (faceW * TILE_FACE_H) / TILE_FACE_W;
  const cardW = Math.round((faceW * TILE_IMG_W) / TILE_FACE_W);
  const cardH = Math.round((faceW * TILE_IMG_H) / TILE_FACE_W);
  const tileLayer = Math.max(1, Math.round(faceW * TILE_LAYER_RATIO));
  const boardW = wFaces * faceW;
  const boardH = hFaces * faceW;
  // Connect: the free room round the grid (Layout.margin, mahjongFace)
  const margin = Math.round((l.margin ?? 0) * faceW);
  // the layout's top left corner (layer 0), the top layers reaching up and left of it
  const tableauX0 = Math.round((width - boardW) / 2 + layers * tileLayer) + margin;
  // the discard pile (discardSpot) in the real pixel steps; the board makes room for it above the top layer
  const clear = Math.max(DISCARD_GAP_MIN, faceW * DISCARD_CLEAR) / faceW;
  const spot = discardSpot(l, clear, tileLayer / faceW, faceH / faceW);
  const above = Math.round(Math.max(0, -layers * tileLayer - spot.y * faceW));
  const tableauY = Math.round(Math.max(padY, (height - boardH) / 2) + layers * tileLayer + above) + margin;
  const foundX0 = Math.round(Math.min(tableauX0 + spot.x * faceW, width - padX / 2 - faceW * BODY_W));
  const foundY0 = Math.round(tableauY + spot.y * faceW);
  return {
    cardW,
    cardH,
    gap: 0,
    pitch: faceW,
    fan: 0,
    straightFan: false,
    stepMax: 0,
    stepMin: 0,
    topY: tableauY,
    tableauY,
    tableauX0,
    stockX: tableauX0,
    wasteX: tableauX0,
    foundX0,
    // one discard pile: a pitch of a whole picture keeps its slot whole (a narrower pitch trims the slot, geometryVars
    // --found-slot-w; owner 2026-10-24 on the iPad: "ucięte")
    foundPitch: cardW,
    foundCols: 1,
    foundY0,
    foundRowPitch: 0,
    wasteY: tableauY,
    cells: 0,
    cellX0: tableauX0,
    cellPitch: faceW,
    cellCols: 1,
    cellY0: tableauY,
    cellRowPitch: 0,
    side: false,
    colH: cardH,
    cols: across,
    found: 1,
    hasWaste: false,
    pyrStep: 0,
    tileHalfW: faceW / 2,
    tileHalfH: faceH / 2,
    tileLayer,
  };
}

/** The CSS custom properties board.css reads. */
export function geometryVars(g: Geometry): Record<string, string> {
  return {
    '--card-w': `${g.cardW}px`,
    '--card-h': `${g.cardH}px`,
    '--pitch': `${g.pitch}px`,
    '--fan': `${g.fan}px`,
    '--step-max': `${g.stepMax}px`,
    '--step-min': `${g.stepMin}px`,
    '--top-y': `${g.topY}px`,
    '--tableau-y': `${g.tableauY}px`,
    '--tableau-x0': `${g.tableauX0}px`,
    '--stock-x': `${g.stockX}px`,
    '--waste-x': `${g.wasteX}px`,
    '--found-x0': `${g.foundX0}px`,
    '--found-pitch': `${g.foundPitch}px`,
    // foundations closer than a card: each empty slot as wide as its pitch less a small gap, so the slots do not
    // overlap into one dark band (owner 2026-10-24, a phone's Double Klondike)
    '--found-slot-w': `${g.foundPitch < g.cardW ? Math.max(1, g.foundPitch - Math.max(2, Math.round(g.cardW * 0.06))) : g.cardW}px`,
    '--found-cols': `${g.foundCols}`,
    '--found-y0': `${g.foundY0}px`,
    '--found-row-pitch': `${g.foundRowPitch}px`,
    '--waste-y': `${g.wasteY}px`,
    '--cell-x0': `${g.cellX0}px`,
    '--cell-pitch': `${g.cellPitch}px`,
    '--cell-cols': `${g.cellCols}`,
    '--cell-y0': `${g.cellY0}px`,
    '--cell-row-pitch': `${g.cellRowPitch}px`,
    '--col-h': `${g.colH}px`,
    '--pyr-step': `${g.pyrStep}px`,
    '--tile-hw': `${g.tileHalfW}px`,
    '--tile-hh': `${g.tileHalfH}px`,
    '--tile-layer': `${g.tileLayer}px`,
  };
}
